/**
 * Formulation unique de la confirmation d'annulation d'une candidature.
 *
 * Le texte vit ici et non dans les composants : il est affiché à l'identique
 * depuis la liste des candidatures et depuis le détail d'une candidature. Deux
 * copies auraient dérivé dès la première reformulation.
 */

export interface InfosAnnulation {
  /** ISO 8601, horodatage écrit par la base sur la ligne d'audit. */
  at?: string | null;
  /** Nom figé au moment de l'acte. Absent sur les annulations antérieures à sa capture. */
  par?: string | null;
}

/**
 * Ligne factuelle des bandeaux d'annulation : quand, et par qui.
 *
 * Affichée à l'identique en tête de page ET dans la section "Dépôt de la
 * candidature et de l'offre", où elle remplace le badge "Offre déposée avec
 * succès" devenu faux.
 *
 * Dégrade proprement plutôt que d'afficher un trou : sans nom, la phrase
 * s'arrête à la date ; sans date, elle se réduit au constat.
 */
export function ligneAnnulation(infos?: InfosAnnulation | null): string {
  const base = "Dépôt de l'offre annulée";

  if (!infos?.at) {
    return `${base}.`;
  }

  const d = new Date(infos.at);
  if (isNaN(d.getTime())) {
    return `${base}.`;
  }

  const date = d.toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Europe/Paris',
  });
  const heure = d
    .toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
    .replace(':', 'h');

  const parQui = infos.par ? ` par ${infos.par}` : '';

  return `${base} le ${date} à ${heure}${parQui}`;
}

interface CandidatureNumerotee {
  id: number;
  identifiant_candidature?: number | null;
}

/**
 * Numéro montré au prestataire : l'identifiant métier quand il existe, sinon
 * l'id technique. C'est déjà la règle appliquée par l'en-tête du détail, on ne
 * lui en présente pas un autre au moment de confirmer.
 */
export function numeroCandidature(candidature: CandidatureNumerotee): number {
  return candidature.identifiant_candidature ?? candidature.id;
}

export function titreAnnulationCandidature(numero: number): string {
  return `Annuler la candidature N°${numero}`;
}

/**
 * @param numero Numéro affiché de la candidature.
 * @param lot    Libellé du lot SANS le préfixe "lot" : "102 — Maîtrise d'oeuvre".
 *               Vide si le lot n'est pas connu : la phrase reste correcte.
 */
export function texteAnnulationCandidature(numero: number, lot: string): string {
  const pourLeLot = lot ? ` pour le lot ${lot}` : '';

  return `Cette action est DÉFINITIVE et IRRÉVERSIBLE. `
    + `La candidature N°${numero}${pourLeLot} restera visible sur votre espace `
    + `mais sera considérée comme annulée et non déposée. `
    + `Vous pourrez ensuite déposer une nouvelle candidature sur ce même lot technique. `
    + `Confirmez-vous l'annulation ?`;
}
